import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowUpRight,
  CalendarCheck2,
  CheckCircle,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  Send,
} from 'lucide-react';
import { ScrollReveal } from '@/components/ui/ScrollReveal';
import { Container, Section } from '@/components/layout/Container';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { resolveApiBaseUrl } from '@/lib/utils';
import { useBookingCallToAction } from '@/hooks/useBookingCallToAction';

const API_URL = resolveApiBaseUrl(import.meta.env.VITE_API_URL);

const contactInfo = [
  {
    icon: <Phone className="w-5 h-5" />,
    label: 'Phone',
    value: '+47 958 38 600',
    href: 'tel:+4795838600',
  },
  {
    icon: <Mail className="w-5 h-5" />,
    label: 'Email',
    value: 'christer@invested.no',
    href: 'mailto:christer@invested.no',
  },
  {
    icon: <MapPin className="w-5 h-5" />,
    label: 'Location',
    value: 'Oslo, Norway',
    href: '#',
  },
  {
    icon: <Linkedin className="w-5 h-5" />,
    label: 'LinkedIn',
    value: 'Connect on LinkedIn',
    href: 'https://www.linkedin.com/in/christertl/',
    external: true,
  },
];

const inquiryTypes = [
  { value: 'advisory', label: 'Advisory Consultation' },
  { value: 'project', label: 'Project-Based Engagement' },
  { value: 'board', label: 'Board/Executive Role' },
  { value: 'ai', label: 'AI Integration Support' },
  { value: 'other', label: 'Other Inquiry' },
];

const tierToInquiryType = {
  advisory: 'advisory',
  project: 'project',
  executive: 'board',
};

const buildSchedulePrompt = (tier?: string) => {
  const tierLabel =
    tier === 'project'
      ? 'project-based engagement'
      : tier === 'executive'
        ? 'executive/board advisory engagement'
        : 'advisory consultation';

  return [
    `I would like to schedule a ${tierLabel}.`,
    '',
    'Scope summary:',
    '- Organization:',
    '- Objective:',
    '- Key challenge:',
    '- Preferred format (remote/on-site):',
    '- Desired timeline:',
    '- Stakeholders involved:',
    '',
    'Please propose next available options and recommended engagement structure.',
  ].join('\n');
};

interface ContactProps {
  standalone?: boolean;
}

export const Contact: React.FC<ContactProps> = ({ standalone = false }) => {
  const { directBookingEnabled, triggerBooking } = useBookingCallToAction(import.meta.env.VITE_BOOKING_URL);
  const [formStartedAt] = useState<number>(() => Date.now());
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    company: '',
    inquiryType: '',
    message: '',
    website: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const bookingLabel = directBookingEnabled ? 'Book Intro Call' : 'Request Intro Call';

  const queryParams = useMemo(
    () =>
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search)
        : new URLSearchParams(),
    []
  );

  useEffect(() => {
    const intent = queryParams.get('intent');
    const tier = queryParams.get('tier') ?? '';

    if (intent !== 'schedule-consultation') return;

    setFormState((prev) => ({
      ...prev,
      inquiryType: prev.inquiryType || tierToInquiryType[tier as keyof typeof tierToInquiryType] || 'advisory',
      message: prev.message.trim() ? prev.message : buildSchedulePrompt(tier),
    }));
  }, [queryParams]);

  const applySchedulePrompt = () => {
    const tier = queryParams.get('tier') ?? '';
    setFormState((prev) => ({
      ...prev,
      inquiryType: prev.inquiryType || tierToInquiryType[tier as keyof typeof tierToInquiryType] || 'advisory',
      message: buildSchedulePrompt(tier),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formState,
          formStartedAt,
        }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to send message');
      }

      setIsSubmitted(true);
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'Unable to submit your message right now. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormState((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <Section id={standalone ? undefined : 'contact'} className={standalone ? 'pt-24 md:pt-28' : ''}>
      <Container>
        <ScrollReveal className="text-center mb-12">
          <span className="text-caption text-apple-blue uppercase tracking-wider font-medium">
            Direct Inquiries
          </span>
          <h2 className="text-h1 md:text-h1 font-bold mt-3 mb-4">
            Let&apos;s <span className="gradient-text">Connect</span>
          </h2>
          <p className="text-body text-text-secondary max-w-2xl mx-auto">
            Serious, structured engagements only. I look forward to discussing how my expertise can
            support your organization&apos;s strategic objectives.
          </p>
        </ScrollReveal>

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-12">
          <ScrollReveal direction="left" className="lg:col-span-2">
            <div className="space-y-6">
              <div>
                <h3 className="text-h4 font-semibold mb-3">Contact Information</h3>
                <p className="text-body-s text-text-secondary">
                  Available for international engagements. Typical response within 48 hours for qualified inquiries.
                </p>
              </div>

              <div className="rounded-xl border border-apple-blue/30 bg-apple-blue/5 p-4">
                <p className="text-caption uppercase tracking-wider text-apple-blue mb-2">
                  Fastest Route
                </p>
                <p className="text-body-s text-text-secondary mb-3">
                  {directBookingEnabled
                    ? 'Book a 20-minute intro call directly to confirm fit and next steps.'
                    : 'Request a 20-minute intro call and I will reply with the best next step.'}
                </p>
                <Button
                  variant="primary"
                  fullWidth
                  onClick={triggerBooking}
                  icon={<CalendarCheck2 className="w-4 h-4" />}
                >
                  {bookingLabel}
                </Button>
              </div>

              <div className="space-y-3">
                {contactInfo.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    target={item.external ? '_blank' : undefined}
                    rel={item.external ? 'noreferrer' : undefined}
                    className="flex items-center gap-4 p-4 rounded-xl bg-bg-tertiary border border-border-subtle hover:border-apple-blue/30 transition-colors group"
                  >
                    <div className="w-11 h-11 rounded-lg bg-bg-primary flex items-center justify-center text-apple-blue group-hover:bg-apple-blue/10 transition-colors">
                      {item.icon}
                    </div>
                    <div>
                      <p className="text-caption text-text-tertiary uppercase tracking-wider">{item.label}</p>
                      <p className="text-body-s font-medium text-text-primary group-hover:text-apple-blue transition-colors">
                        {item.value}
                      </p>
                    </div>
                  </a>
                ))}
              </div>

              <div className="pt-5 border-t border-border-subtle">
                <p className="text-caption text-text-tertiary uppercase tracking-wider mb-3">Languages</p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">Norwegian (Native)</Badge>
                  <Badge variant="outline">English (Fluent)</Badge>
                  <Badge variant="outline">Polish (Near-fluent)</Badge>
                  <Badge variant="outline">German (Working)</Badge>
                </div>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="right" className="lg:col-span-3">
            <div className="bg-bg-tertiary rounded-3xl border border-border-subtle p-6 md:p-7">
              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-10"
                >
                  <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-7 h-7 text-success" />
                  </div>
                  <h3 className="text-h3 font-semibold mb-2">Message Sent</h3>
                  <p className="text-body-s text-text-secondary">
                    Thank you for your inquiry. I will review your message and respond within 48 hours.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="sr-only" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      value={formState.website}
                      onChange={handleChange}
                      autoComplete="off"
                      tabIndex={-1}
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-body-s font-medium text-text-primary mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formState.name}
                        onChange={handleChange}
                        className="w-full bg-bg-primary border border-border-subtle rounded-xl px-4 py-3 text-body-s text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-apple-blue focus:ring-1 focus:ring-apple-blue transition-all"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-body-s font-medium text-text-primary mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formState.email}
                        onChange={handleChange}
                        className="w-full bg-bg-primary border border-border-subtle rounded-xl px-4 py-3 text-body-s text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-apple-blue focus:ring-1 focus:ring-apple-blue transition-all"
                        placeholder="you@company.com"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="company" className="block text-body-s font-medium text-text-primary mb-2">
                        Company / Organization
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formState.company}
                        onChange={handleChange}
                        className="w-full bg-bg-primary border border-border-subtle rounded-xl px-4 py-3 text-body-s text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-apple-blue focus:ring-1 focus:ring-apple-blue transition-all"
                        placeholder="Company name"
                      />
                    </div>
                    <div>
                      <label htmlFor="inquiryType" className="block text-body-s font-medium text-text-primary mb-2">
                        Inquiry Type *
                      </label>
                      <select
                        id="inquiryType"
                        name="inquiryType"
                        required
                        value={formState.inquiryType}
                        onChange={handleChange}
                        className="w-full bg-bg-primary border border-border-subtle rounded-xl px-4 py-3 text-body-s text-text-primary focus:outline-none focus:border-apple-blue focus:ring-1 focus:ring-apple-blue transition-all appearance-none cursor-pointer"
                      >
                        <option value="">Select type</option>
                        {inquiryTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <label htmlFor="message" className="block text-body-s font-medium text-text-primary">
                        Message *
                      </label>
                      <button
                        type="button"
                        onClick={applySchedulePrompt}
                        className="text-caption text-apple-blue hover:underline"
                      >
                        Generate Schedule Prompt
                      </button>
                    </div>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      value={formState.message}
                      onChange={handleChange}
                      className="w-full bg-bg-primary border border-border-subtle rounded-xl px-4 py-3 text-body-s text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-apple-blue focus:ring-1 focus:ring-apple-blue transition-all resize-none"
                      placeholder="Describe your inquiry and goals..."
                    />
                  </div>

                  {submitError && <p className="text-body-s text-warning">{submitError}</p>}

                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    fullWidth
                    isLoading={isSubmitting}
                    icon={<Send className="w-4 h-4" />}
                  >
                    Send Inquiry
                  </Button>

                  <p className="text-caption text-text-tertiary text-center">
                    All inquiries are handled under strict confidentiality.
                  </p>

                  <div className="grid sm:grid-cols-3 gap-3 pt-2">
                    <div className="rounded-lg border border-border-subtle bg-bg-primary p-3">
                      <p className="text-caption uppercase tracking-wider text-apple-blue mb-1">1. Review</p>
                      <p className="text-caption text-text-tertiary">
                        Inquiry screened for mandate fit and urgency.
                      </p>
                    </div>
                    <div className="rounded-lg border border-border-subtle bg-bg-primary p-3">
                      <p className="text-caption uppercase tracking-wider text-apple-blue mb-1">2. Response</p>
                      <p className="text-caption text-text-tertiary">
                        Initial reply with recommendation within 48 hours.
                      </p>
                    </div>
                    <div className="rounded-lg border border-border-subtle bg-bg-primary p-3">
                      <p className="text-caption uppercase tracking-wider text-apple-blue mb-1">3. Kickoff</p>
                      <p className="text-caption text-text-tertiary">
                        Scope, timeline, and deliverables are confirmed.
                      </p>
                    </div>
                  </div>

                  {directBookingEnabled && (
                    <button
                      type="button"
                      onClick={triggerBooking}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-border-subtle px-6 py-3 text-body-s font-medium text-text-primary transition-all duration-200 hover:border-apple-blue/40 hover:bg-bg-primary"
                    >
                      <CalendarCheck2 className="w-4 h-4 text-apple-blue" />
                      <span>Prefer direct booking instead?</span>
                      <ArrowUpRight className="w-4 h-4 text-apple-blue" />
                    </button>
                  )}
                </form>
              )}
            </div>
          </ScrollReveal>
        </div>
      </Container>
    </Section>
  );
};

export default Contact;
